import React, { Children, cloneElement, createContext, useEffect, useMemo, useState } from 'react';
import { bool, func, node, number, shape, string } from 'prop-types';
import { useParams } from 'react-router-dom';

// Utils
import { userRoles } from 'utils/currentUserRoles';
import { useGetAffiliateOptions } from 'hooks';

// Components
import { Box, Drawer, LinearProgress, Typography } from '@mui/material';
import { DrawerLayoutWrapper } from 'common';
import { theme } from 'library';

// Create a context with default values
export const SequencingWizardContext = createContext();

const drawerStyles = () => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
  overflowY: 'unset',
  width: 390,
});

// Create a provider (wrapper that handles the logic)
// This also allows communication to context and update
// state values throughout app with user info
const SequencingWizard = props => {
  const {
    children,
    currentSelection,
    initialStep,
    isLoading,
    setChildren: setchildren,
    // singular function that only opens the dialog on the last step
    setDialogOpen,
    setOpen,
    steps,
    title,
  } = props;

  const { currentAffiliateId, dataCompanyId } = useParams();
  const affiliatesOptions = useGetAffiliateOptions([userRoles.SUBSCRIBER, userRoles.SUPPLIER]);

  const currentAffiliate = affiliatesOptions.find(
    ({ value }) => value === `${currentAffiliateId}-${dataCompanyId}`
  );

  const initialData = useMemo(
    () => ({
      action: '',
      applyOnFutureDate: null,
      changeList: [],
      currentAffiliate,
      currentSelection,
      flow: '',
      futureDate: undefined,
      inventoryOwners: [],
      loadLocations: true,
      loadProducts: true,
      locations: [],
      name: '',
      products: [],
      type: '',
    }),
    [currentAffiliate, currentSelection]
  );

  // Any useStates here can easily be passed to any component
  // via the value prop for the Provider, then accessed
  // through useContext in the children components
  // Global State Data for Wizards
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [openSummary, setOpenSummary] = useState(false);
  const [data, setData] = useState(initialData);
  const stepsProgress = steps?.[data?.flow]?.length
    ? (currentStep / steps[data.flow].length) * 100
    : 0;

  // Finial step in wizard
  const [progress, setProgress] = useState(0);
  const batchUpdating = currentStep === steps[data.flow]?.length && progress !== 100;

  // Handling the step updating and also updating
  // the stepper progress as we move along the wizard
  const next = () => {
    setCurrentStep(prevStep => prevStep + 1);
    setOpenSummary(false);
  };
  const prev = () => {
    setCurrentStep(prevStep => prevStep - 1);
    setOpenSummary(false);
  };

  useEffect(() => {
    const changeList = data.locations.reduce((acc, location) => {
      return [
        ...acc,
        ...location.products
          .filter(productId => data.products.find(p => p.id === productId))
          .map(productId => {
            const product = data.products.find(p => p.id === productId);
            return {
              location: {
                id: location.id,
                nameShort: location.nameShort,
              },
              product: {
                id: product.id,
                nameShort: product.name,
              },
            };
          }),
      ];
    }, []);
    setData(prevData => ({ ...prevData, changeList }));
  }, [data.locations, data.products]);

  // Rendering children components for the wizards
  // functions/variables passed here are passed to
  // every child component, so only pass in global
  // functions and variables that need to be used
  // in every child component
  const components = Children.toArray(children).map(child =>
    cloneElement(child, {
      close: () => setOpen(false),
      next,
      prev,
      setchildren,
    })
  );

  const handleClose = () => (batchUpdating ? setDialogOpen() : setOpen(false));

  const contextValue = useMemo(
    () => ({
      affiliatesOptions,
      batchUpdating,
      data,
      initialData,
      openSummary,
      progress,
      setCurrentStep,
      setData,
      setDialogOpen,
      setOpenSummary,
      setProgress,
    }),
    [affiliatesOptions, batchUpdating, data, initialData, openSummary, progress, setDialogOpen]
  );

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open
      slotProps={{
        paper: { sx: drawerStyles },
      }}
    >
      <DrawerLayoutWrapper
        headerButtonText="CLOSE"
        isLoading={isLoading}
        onHeaderButtonClick={handleClose}
        title={title}
      >
        {currentStep > 0 && (
          <Box borderBottom={`1px solid ${theme.palette.graphic.light}`} p={3} w={1}>
            <LinearProgress
              data-variant="magellanProgressBar"
              value={stepsProgress}
              variant="determinate"
            />
            <Typography>{`${steps[data.flow][currentStep - 1].stepLabel} (${currentStep} of ${
              steps[data.flow].length
            })`}</Typography>
          </Box>
        )}
        <SequencingWizardContext.Provider
          // Any item passed in this value prop
          // can be accessed in any child component
          // this should be used for variables/functions
          // that need to be accessed in individual
          // components, not for global variables/functions
          value={contextValue}
        >
          {components[currentStep]}
        </SequencingWizardContext.Provider>
      </DrawerLayoutWrapper>
    </Drawer>
  );
};

SequencingWizard.propTypes = {
  children: node.isRequired,
  currentSelection: shape({}).isRequired,
  initialStep: number,
  isLoading: bool.isRequired,
  setChildren: func.isRequired,
  setDialogOpen: func.isRequired,
  setOpen: func.isRequired,
  steps: shape({}).isRequired,
  title: string.isRequired,
};

SequencingWizard.defaultProps = {
  initialStep: 1,
};

export default SequencingWizard;
